import { purgeNavStorage } from '../nav/session';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Brand } from '../ui/Brand';
import { VersionBadge } from '../version/VersionBadge';
import { ME_KEY, useMe } from './useMe';

const messages: Record<number, string> = {
  401: 'Nieprawidłowy e-mail lub hasło',
  429: 'Zbyt wiele nieudanych prób logowania. Spróbuj ponownie za kilka minut.',
};

export function LoginPage() {
  const { me, isLoading } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!isLoading && me) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/login', { method: 'POST', body: { email, password } });
      purgeNavStorage(); // P13: nothing of a previous account's drafts or places
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      navigate('/', { replace: true });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      setError(messages[status] ?? (e instanceof ApiError ? e.message : 'Nie udało się zalogować. Spróbuj ponownie.'));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login-case" onSubmit={submit} data-feature-id="identity.login" noValidate>
        <div className="login-mark">
          <Brand large />
        </div>
        <div className="login-body">
          <h1>Zaloguj się</h1>
          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          <label className="field">
            <span>E-mail</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Hasło</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button className="btn primary block" type="submit" disabled={busy || !email || !password}>
            {busy ? 'Logowanie…' : 'Zaloguj się'}
          </button>
        </div>
      </form>
      <footer className="login-version">
        <VersionBadge />
      </footer>
    </main>
  );
}
